import { applyTokenCssVars } from "./cssVars";
import { colorPalettes, type ThemeName, type ThemePreference } from "./color";

const THEME_STORAGE_KEY = "galileo.einstein.theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";
const THEME_COLOR_META = 'meta[name="theme-color"][data-runtime-theme-color="true"]';

export interface ResolvedTheme {
    readonly preference: ThemePreference;
    readonly theme: ThemeName;
}

export interface ThemeController {
    readonly getSnapshot: () => ResolvedTheme;
    readonly setPreference: (preference: ThemePreference) => void;
    readonly subscribe: (notify: () => void) => () => void;
    readonly dispose: () => void;
}

let activeController: ThemeController | undefined;

export function isThemeName(value: string): value is ThemeName {
    return value === "dark" || value === "light";
}

export function isThemePreference(value: string): value is ThemePreference {
    return value === "system" || isThemeName(value);
}

export function readStoredThemePreference(storage: Storage | undefined): ThemePreference {
    if (storage === undefined) {
        return "system";
    }

    const stored = storage.getItem(THEME_STORAGE_KEY);
    return stored !== null && isThemePreference(stored) ? stored : "system";
}

export function resolveThemePreference(
    preference: ThemePreference,
    systemPrefersDark: boolean,
): ThemeName {
    if (preference === "system") {
        return systemPrefersDark ? "dark" : "light";
    }
    return preference;
}

export function applyResolvedTheme(target: HTMLElement, resolved: ResolvedTheme): void {
    target.dataset.theme = resolved.theme;
    target.dataset.themePreference = resolved.preference;
    target.style.colorScheme = resolved.theme;
    applyTokenCssVars(target, resolved.theme);
    updateThemeColorMeta(resolved.theme);
}

export function initializeTheme(target: HTMLElement = document.documentElement): ThemeController {
    activeController?.dispose();

    let preference = readStoredThemePreference(getStorage());
    const listeners = new Set<() => void>();

    const mediaQuery =
        typeof window !== "undefined" && typeof window.matchMedia === "function"
            ? window.matchMedia(DARK_QUERY)
            : undefined;

    const computeSnapshot = (): ResolvedTheme => ({
        preference,
        theme: resolveThemePreference(preference, mediaQuery?.matches ?? true),
    });
    let snapshot = computeSnapshot();

    const current = (): ResolvedTheme => snapshot;

    const refreshSnapshot = (): boolean => {
        const next = computeSnapshot();
        const changed = next.preference !== snapshot.preference || next.theme !== snapshot.theme;
        snapshot = next;
        return changed;
    };

    const notify = (): void => {
        for (const listener of listeners) {
            listener();
        }
    };

    const apply = (shouldNotify: boolean): void => {
        applyResolvedTheme(target, current());
        if (shouldNotify) {
            notify();
        }
    };

    const onSystemThemeChange = (): void => {
        if (preference === "system") {
            apply(refreshSnapshot());
        }
    };

    mediaQuery?.addEventListener("change", onSystemThemeChange);
    apply(false);

    activeController = {
        getSnapshot: current,
        setPreference(nextPreference) {
            preference = nextPreference;
            const storage = getStorage();
            if (storage !== undefined) {
                storage.setItem(THEME_STORAGE_KEY, nextPreference);
            }
            apply(refreshSnapshot());
        },
        subscribe(notifyListener) {
            listeners.add(notifyListener);
            return () => {
                listeners.delete(notifyListener);
            };
        },
        dispose() {
            mediaQuery?.removeEventListener("change", onSystemThemeChange);
            listeners.clear();
            if (activeController === this) {
                activeController = undefined;
            }
        },
    };

    return activeController;
}

export function getThemeController(
    target: HTMLElement = document.documentElement,
): ThemeController {
    return activeController ?? initializeTheme(target);
}

function getStorage(): Storage | undefined {
    if (typeof window === "undefined") {
        return undefined;
    }

    try {
        return window.localStorage;
    } catch {
        return undefined;
    }
}

function updateThemeColorMeta(theme: ThemeName): void {
    if (typeof document === "undefined") {
        return;
    }

    let meta = document.head.querySelector<HTMLMetaElement>(THEME_COLOR_META);
    if (meta === null) {
        meta = document.createElement("meta");
        meta.name = "theme-color";
        meta.dataset.runtimeThemeColor = "true";
        document.head.appendChild(meta);
    }

    meta.content = colorPalettes[theme].bg.canvas;
}
